import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosInstance } from "axios";
import { ILocation } from "../interfaces/ILocation";
import { LocationService } from "../services/locationService";
import { RootState } from "./store";
import { ILocationList } from "../interfaces/ILocationList";
import { IRoom } from "../interfaces/IRoom";
import { IRoomList } from "../interfaces/IRoomList";
import { IPagedFilterRequest } from "../interfaces/IPagedFilterResults";

export const selectLoading = (state: RootState) => state.locations.loading;
export const selectLocations = (state: RootState) => state.locations.locations;
export const selectFullLocationById = (state: RootState, id: string) =>
    state.locations.fullLocations.find(loc => loc.id === id) || null;

// Async actions
export const fetchLocations = createAsyncThunk<
    {
        items: ILocationList[];
        totalCount?: number;
        isPaged: boolean;
    },
    { request?: IPagedFilterRequest; apiClient: AxiosInstance },
    { state: RootState }
>(
    "locations/fetchPaged",
    async ({ request = {}, apiClient }) => {
        const locationService = new LocationService(apiClient);
        const isPagedRequest = request.page !== undefined && request.pageSize !== undefined;

        if (isPagedRequest) {
            const response = await locationService.getAllLocation(request);

            return {
                isPaged: true,
                totalCount: response.totalCount,
                items: response.items.map((loc): ILocationList => ({
                    id: loc.id ?? "",
                    name: loc.name,
                    city: loc.city,
                    country: loc.country,
                    region: loc.region,
                    streetAddress: loc.streetAddress,
                    postalCode: loc.postalCode,
                    rooms: (loc.rooms ?? []).map(toRoomList),
                }))
            };
        } else {
            const allItems = await locationService.getAll<ILocation[]>();

            return {
                isPaged: false,
                items: allItems.map((loc): ILocationList => ({
                    id: loc.id ?? "",
                    name: loc.name,
                    city: loc.city,
                    country: loc.country,
                    region: loc.region,
                    streetAddress: loc.streetAddress,
                    postalCode: loc.postalCode,
                    rooms: (loc.rooms ?? []).map(toRoomList),
                }))
            };
        }
    }
);





export const fetchLocationById = createAsyncThunk<ILocation, { locationId: string; apiClient: AxiosInstance }>(
    "locations/fetchById",
    async ({ locationId, apiClient }) => {
        const locationService = new LocationService(apiClient);
        return await locationService.get(locationId);
    }
);

export const addLocation = createAsyncThunk<ILocation, { location: ILocation; apiClient: AxiosInstance }>(
    "locations/add",
    async ({ location, apiClient }) => {
        const locationService = new LocationService(apiClient);
        return await locationService.create(location);
    }
);

export const updateLocation = createAsyncThunk<ILocation, { locationId: string; updatedLocation: ILocation; apiClient: AxiosInstance }>(
    "locations/update",
    async ({ locationId, updatedLocation, apiClient }) => {
        const locationService = new LocationService(apiClient);
        return await locationService.update(locationId, updatedLocation);
    }
);

export const deleteLocation = createAsyncThunk<string, { locationId: string; apiClient: AxiosInstance }>(
    "locations/delete",
    async ({ locationId, apiClient }) => {
        const locationService = new LocationService(apiClient);
        await locationService.delete(locationId);
        return locationId;
    }
);

const locationsSlice = createSlice({
    name: "locations",
    initialState: {
        locations: [] as ILocationList[],
        fullLocations: [] as ILocation[],
        loading: false,
        lastFetched: 0,
        totalCount: 0
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchLocations.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchLocations.fulfilled, (state, action) => {
                state.loading = false;
                state.locations = action.payload.items;
                state.lastFetched = Date.now();

                if (action.payload.isPaged) {
                    state.totalCount = action.payload.totalCount ?? 0;
                } else {
                    state.totalCount = 0; // Or: state.totalCount = action.payload.items.length;
                }
            })

            .addCase(fetchLocationById.fulfilled, (state, action) => {
                const idx = state.fullLocations.findIndex(loc => loc.id === action.payload.id);
                if (idx >= 0) {
                    state.fullLocations[idx] = action.payload;
                } else {
                    state.fullLocations.push(action.payload);
                }
            })
            .addCase(addLocation.fulfilled, (state, action) => {
                const locationListItem: ILocationList = {
                    id: action.payload.id ?? "",
                    name: action.payload.name,
                    city: action.payload.city,
                    country: action.payload.country,
                    region: action.payload.region,
                    streetAddress: action.payload.streetAddress,
                    postalCode: action.payload.postalCode,
                    rooms: (action.payload.rooms ?? []).map(toRoomList)
                };
                state.locations.push(locationListItem);
                state.fullLocations.push(action.payload);
                state.totalCount += 1;
            })
            .addCase(updateLocation.fulfilled, (state, action) => {
                const locationListItem: ILocationList = {
                    id: action.payload.id ?? "",
                    name: action.payload.name,
                    city: action.payload.city,
                    country: action.payload.country,
                    region: action.payload.region,
                    streetAddress: action.payload.streetAddress,
                    postalCode: action.payload.postalCode,
                    rooms: (action.payload.rooms ?? []).map(toRoomList)
                };
                state.locations = state.locations.map(loc =>
                    loc.id === action.payload.id ? locationListItem : loc
                );

                const idx = state.fullLocations.findIndex(loc => loc.id === action.payload.id);
                if (idx >= 0) {
                    state.fullLocations[idx] = action.payload;
                } else {
                    state.fullLocations.push(action.payload);
                }
            })
            .addCase(deleteLocation.fulfilled, (state, action) => {
                state.locations = state.locations.filter(loc => loc.id !== action.payload);
                state.fullLocations = state.fullLocations.filter(loc => loc.id !== action.payload);
                state.totalCount -= 1;
            });
    }
});

function toRoomList(room: IRoom): IRoomList {
    return {
        id: room.id,
        name: room.name,
        capacity: room.capacity,
        facilities: room.facilities,
        accessibilityFeatures: room.accessibilityFeatures
    };
}

export default locationsSlice.reducer;